import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowUpRight, BookOpen, FileText, Filter } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Public blog index — lists published posts.
 * Filterable between long-form Essays and short Field Notes.
 *
 * Reads from `GET /api/blog/posts?format=…` so the format query is server-side
 * filtered (no client-side hiding) — keeps payload small and pagination-ready.
 */
export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [filter, setFilter] = useState("all"); // "all" | "essay" | "fieldnote"
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const q = filter === "all" ? "" : `?format=${filter}`;
        const { data } = await axios.get(`${API}/blog/posts${q}`);
        if (!cancelled) setPosts(data);
      } catch (e) {
        if (!cancelled) setError("Couldn't load posts right now. Please try again.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [filter]);

  return (
    <main data-testid="page-blog" className="pt-24 bg-[var(--bone)] min-h-screen">
      {/* HERO */}
      <section className="bg-[var(--navy-950)] text-[var(--bone)] py-20 lg:py-28 relative overflow-hidden">
        <div className="grain absolute inset-0 opacity-50"></div>
        <div className="relative max-w-6xl mx-auto px-6 lg:px-10">
          <div className="eyebrow-light" style={{ color: "var(--gold-soft)" }}>
            Field Notes & Essays
          </div>
          <h1 className="font-display text-[44px] md:text-[64px] leading-[1.02] mt-6 max-w-4xl">
            Notes From The Work — For The Leaders <span style={{ color: "var(--gold-soft)" }}>Doing The Work.</span>
          </h1>
          <p className="mt-7 max-w-2xl text-[16px] leading-[1.7] text-[var(--navy-100)]/85">
            Long-form essays and short field notes on workforce change, AI integration, operational
            alignment, and what it takes to build organizations that hold — without losing the people
            inside them.
          </p>
        </div>
      </section>

      {/* FILTER + LIST */}
      <section className="py-14 lg:py-20">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          {/* Format filter */}
          <div className="flex flex-wrap items-center gap-2 lg:gap-3 mb-12">
            <div
              className="inline-flex items-center gap-2 text-[10px] tracking-[0.32em] uppercase font-bold mr-2"
              style={{ color: "var(--muted)" }}
            >
              <Filter size={12} /> Format
            </div>
            {[
              { id: "all", label: "All", I: null },
              { id: "essay", label: "Essays", I: BookOpen },
              { id: "fieldnote", label: "Field Notes", I: FileText },
            ].map((f) => (
              <button
                key={f.id}
                data-testid={`blog-filter-${f.id}`}
                onClick={() => setFilter(f.id)}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-[11px] tracking-[0.22em] uppercase font-semibold border transition-colors ${
                  filter === f.id
                    ? "bg-[var(--navy-900)] text-[var(--bone)] border-[var(--navy-900)]"
                    : "bg-transparent text-[var(--navy-900)] border-[rgba(10,31,68,0.18)] hover:bg-[var(--navy-50)]"
                }`}
              >
                {f.I && <f.I size={12} />}
                {f.label}
              </button>
            ))}
          </div>

          {loading && (
            <div className="text-[var(--muted)] text-[14px]" data-testid="blog-loading">
              Loading…
            </div>
          )}

          {error && !loading && (
            <div className="text-red-700 text-[14px]" data-testid="blog-error">
              {error}
            </div>
          )}

          {!loading && !error && posts.length === 0 && (
            <div
              data-testid="blog-empty"
              className="py-20 text-center text-[var(--muted)] border-y border-[rgba(10,31,68,0.08)]"
            >
              <div className="text-[12px] tracking-[0.28em] uppercase">No posts yet</div>
              <div className="mt-2 text-[14px]">The first piece is on its way.</div>
            </div>
          )}

          {!loading && !error && posts.length > 0 && (
            <ul className="space-y-1 -mt-1" data-testid="blog-list">
              {posts.map((p) => (
                <li key={p.id}>
                  <Link
                    to={`/blog/${p.slug}`}
                    data-testid={`blog-card-${p.slug}`}
                    className="group block py-8 border-t border-[rgba(10,31,68,0.10)] hover:bg-white transition-colors px-2 -mx-2"
                  >
                    <div className="grid lg:grid-cols-12 gap-4 lg:gap-10 items-start">
                      <div className="lg:col-span-2 flex flex-col gap-1">
                        <span
                          className="text-[9px] tracking-[0.32em] uppercase font-bold"
                          style={{ color: p.format === "essay" ? "var(--gold)" : "var(--accent-gold)" }}
                        >
                          {p.format === "essay" ? "Essay" : "Field Note"}
                        </span>
                        <span className="text-[11px] tracking-[0.16em] uppercase text-[var(--muted)]">
                          {formatDate(p.published_at || p.created_at)}
                        </span>
                      </div>
                      <div className="lg:col-span-8">
                        <h2 className="font-display text-[26px] md:text-[32px] leading-[1.1] text-[var(--navy-900)] group-hover:text-[var(--gold)] transition-colors">
                          {p.title}
                        </h2>
                        {p.subtitle && (
                          <p className="mt-3 text-[15px] leading-[1.7] text-[var(--muted)] max-w-2xl">
                            {p.subtitle}
                          </p>
                        )}
                        {p.tags?.length > 0 && (
                          <div className="mt-4 flex flex-wrap gap-1.5">
                            {p.tags.map((t) => (
                              <span
                                key={t}
                                className="text-[9px] tracking-[0.22em] uppercase text-[var(--muted)] px-2 py-1 border border-[rgba(10,31,68,0.10)] rounded"
                              >
                                {t}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="lg:col-span-2 flex lg:justify-end">
                        <span className="inline-flex items-center gap-1 text-[11px] tracking-[0.22em] uppercase font-semibold text-[var(--navy-900)] group-hover:text-[var(--gold)] transition-colors">
                          Read <ArrowUpRight size={14} />
                        </span>
                      </div>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>
    </main>
  );
}

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return "";
  }
}
